import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import ResizablePanel from '@/components/ResizablePanel';
import { useDeviceDialog } from '@/features/DeviceCreate/DeviceCreate.hooks';
import DeviceCreateDialog from '@/features/DeviceCreate/DeviceCreateDialog';
import DevicesTable from '@/features/DevicesTable/DevicesTable';
import LogSourcesCreateDialog from '@/features/LogSourcesCreate/LogSourcesCreateDialog';
import { useSourceDialog } from '@/features/SourceCreate/SourceCreate.hooks';
import SourceCreateDialog from '@/features/SourceCreate/SourceCreateDialog';
import useDeviceQueryParam, {
  DEVICE_DETAILS_PAGE,
  useDeviceSelectionParams
} from '@/pages/Administration/Devices/Devices.hooks';

import { showAlertAtom } from '../../Administration.store';
import DeviceDetails from '../DeviceDetails';
import useStyles from '../Devices.styles';
import { SyncDevicesDialog } from '../SyncDevices/SyncDevicesDialog';
import useCustomNotification, {
  type CustomNotificationProps
} from '../SyncDevices/useCustomNotification';

const AllDevices = () => {
  const { hasDetailSelection } = useDeviceSelectionParams();
  const { classes } = useStyles({
    deviceDetailsCollapsed: !hasDetailSelection
  });
  const { open: showSourcePopup } = useSourceDialog();
  const { open: showDevicePopup } = useDeviceDialog();

  //-----------------------------------------------------------------

  const sizes = ['75%', '25%'];

  const { removeFilter, updateSearchParams } = useDeviceQueryParam();
  const [showAlert] = useAtom(showAlertAtom);
  const {
    ConfirmationModal: CustomNotificationModal,
    confirm: ShowCustomNotification
  } = useCustomNotification({
    title: 'Devices',
    message:
      'CRUD operations are allowed only for the following organizations in the QA and Staging environments: "Test Pondurance", "Pondurance Demo-1", "Pondurance Demo-2", and "Marquardt LLC"',
    severity: 'info',
    buttonType: 'Close'
  } as CustomNotificationProps);

  const closeDeviceDetail = useCallback(() => {
    removeFilter();
  }, [removeFilter]);

  useEffect(() => {
    if (showAlert) {
      ShowCustomNotification();
    }
  }, [showAlert]);

  useEffect(() => {
    updateSearchParams({ pg: DEVICE_DETAILS_PAGE }, { replace: true });
  }, [updateSearchParams]);

  const devicesTableContent = useMemo(
    () => (
      <DevicesTable
        closeDeviceDetail={closeDeviceDetail}
        detailPanelVisible={hasDetailSelection}
        isMainDevicePage
      />
    ),
    [closeDeviceDetail, hasDetailSelection]
  );

  const deviceDetailsContent = useMemo(
    () => (
      <DeviceDetails closeDeviceDetail={closeDeviceDetail} isMainDevicePage />
    ),
    [closeDeviceDetail]
  );

  const panels = useMemo(
    () => [
      {
        content: devicesTableContent,
        id: 'devices-table',
        order: 1,
        minSize: hasDetailSelection ? '40%' : '100%',
        maxSize: hasDetailSelection ? '80%' : '100%',
        showResizeHandle: true // Always show handle since we always have 2 panels
      },
      {
        content: deviceDetailsContent,
        id: 'device-details',
        order: 2,
        minSize: hasDetailSelection ? '20%' : '0%',
        maxSize: hasDetailSelection ? '40%' : '0%',
        showResizeHandle: false
      }
    ],
    [hasDetailSelection, devicesTableContent, deviceDetailsContent]
  );

  return (
    <>
      <Box className={classes.resizablePanelHeighFix}>
        <ResizablePanel sizes={sizes} panels={panels} />
      </Box>

      {showSourcePopup && (
        <SourceCreateDialog isCustomerCloudSourcePage={false} />
      )}
      {showDevicePopup && <DeviceCreateDialog />}
      <LogSourcesCreateDialog />
      <SyncDevicesDialog />
      <CustomNotificationModal />
    </>
  );
};

export default AllDevices;
